import { getRealUrlPrefix, TypeBoundAxiosInstance, metricsStore } from '@m43/core';
import { Socket, StreamBroadcaster } from '@m43/sockets-mark43';
import { UserResource } from '@mark43/rms-api';
import { StreamMap } from './stream-types';
import { getM43LogRocket } from '../monitoring/logrocket';
import { getAppName } from '../app-name/app-name';
import { logDebug, logError, logWarn } from '../monitoring/log';

export function createSocket(args: {
    apiClient: TypeBoundAxiosInstance;
    streamBroadcaster: StreamBroadcaster<StreamMap>;
}) {
    const { apiClient, streamBroadcaster } = args;
    const appName = getAppName();
    const socket = new Socket({
        socketBaseUrl: `${getRealUrlPrefix().replace('http', 'ws')}/${
            appName === 'cad' ? 'cad' : 'fr'
        }-sync-next/api/entity/`,
        fetchSocketToken: async (): Promise<string> => {
            const response = await apiClient.request<UserResource.GenerateRealtimeToken>({
                method: 'GET',
                url: 'user/realtime',
                service: 'RMS',
            });
            return response.data.data!;
        },
        streamBroadcaster,
        // if the socket has not received a message in this many seconds it shifts into a 'FATAL' status
        messageTimeoutThreshold: 22000,
        messageTimeoutCheckFrequency: 3000,
        onMessage(message, metadata) {
            if (message.streamName === 'gps') return;
            if (metadata && metadata.originatingTraceId) {
                metricsStore.addTrace({
                    ...metadata,
                    websocketReceivedMillis: Date.now(),
                });
            }
        },
        onSocketStatusChange(socketStatusChangeEvent) {
            getM43LogRocket()?.log(`Socket Status ${socketStatusChangeEvent.status}`);
        },
        onWarning(warning) {
            getM43LogRocket()?.log(`Socket Warning`, warning);
            logWarn(warning);
        },
        onFatal(source, eventData) {
            logError(new Error('socket fatal error'), {
                type: `socket_fatal.${source}`,
                eventData: JSON.stringify(eventData),
            });
            getM43LogRocket()?.log(`FATAL Socket Info`, { source });
        },
        onOutOfOrderMessage({ streamName, messagePosition }) {
            logDebug('Socket received message with unexpected last message position', {
                type: 'out-of-order-socket-message',
                streamName: String(streamName),
                messagePosition,
            });
        },
    });
    socket.setApplicationName(appName);
    return socket;
}
